<script setup>
import { usePage } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import TimeField from '@js/components/fields/TimeField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import moment from 'moment';
import { computed, reactive, ref, watch } from 'vue';
import { object, string } from 'yup';

const { task, taskTime, callback } = defineProps({
    task: {
        type: Object,
        required: true,
    },
    taskTime: {
        type: Object,
        required: false,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const currentUser = computed(() => usePage().props.currentUser);

const types = ref(null);

const themes = ref(null);

const loading = ref(true);

const isOpen = ref(false);

const isSubmitting = ref(false);

const form = reactive({
    taskType: null,
    taskTheme: null,
    taskName: task.name,
    date: taskTime ? taskTime.date : moment().format('YYYY-MM-DD'),
    hours: taskTime ? taskTime.hours : '',
    minutes: taskTime ? taskTime.minutes : '',
    user: taskTime
        ? (task.users.find((user) => user.id === taskTime.userId) ??
          task.users[0])
        : (task.users.find((user) => user.id === currentUser.value.id) ??
          task.users[0]),
    description: taskTime ? taskTime.description : '',
});

const validationSchema = object({
    taskType: object().required('Ce champ est obligatoire.'),
    taskTheme: object().required('Ce champ est obligatoire.'),
    taskName: string().required('Ce champ est obligatoire.'),
    date: string().required('Ce champ est obligatoire.'),
    hours: string(),
    minutes: string().required('Ce champ est obligatoire.'),
    user: object().required('Ce champ est obligatoire.'),
    description: string().nullable(),
});

const formattedTaskNamePrefix = computed(() => {
    let namePrefix = `[${task.tenant.name}] [${form.taskType.abbreviation}] [${form.taskTheme.label}]`;

    if (task.recurrentType) {
        namePrefix += ` [REC]`;

        if (task.recurrentType === 'weekly') {
            namePrefix += ` [1X/SEM]`;
        }

        if (task.recurrentType === 'monthly') {
            namePrefix += ` [1X/MOIS]`;
        }

        if (task.recurrentType === 'yearly') {
            namePrefix += ` [1X/AN]`;
        }
    }

    namePrefix += ` -`;

    return namePrefix;
});

function updateData() {
    loading.value = true;

    Promise.all([
        axios.get(route('front.projectManagement.task.getTaskTypes')),
        axios.get(route('front.projectManagement.task.getTaskThemes')),
    ])
        .then((response) => {
            types.value = response[0].data;
            themes.value = response[1].data;

            form.taskType = types.value.find(
                (type) => type.value === task.type,
            );

            form.taskTheme = themes.value.find(
                (theme) => theme.value === task.theme,
            );
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.task.postTaskTime',
                taskTime
                    ? { task: task.id, taskTime: taskTime.id }
                    : { task: task.id },
            ),
            {
                taskType: form.taskType.value,
                taskTheme: form.taskTheme.value,
                taskName: form.taskName,
                date: form.date,
                hours: form.hours ?? '00',
                minutes: form.minutes,
                user_id: form.user.id,
                description: form.description,
            },
        )
        .then(({ data }) => {
            isOpen.value = false;

            if (callback) {
                callback();
            }

            if (taskTime) {
                form.taskType = types.value.find(
                    (type) => type.value === data.task.type,
                );
                form.taskTheme = themes.value.find(
                    (theme) => theme.value === data.task.theme,
                );
                form.taskName = data.task.name;
                form.date = data.date;
                form.hours = data.hours;
                form.minutes = data.minutes;
                form.user = task.users.find((user) => user.id === data.userId);
                form.description = data.description;
            } else {
                form.taskType = types.value[0];
                form.taskTheme = themes.value[0];
                form.taskName = '';
                form.date = moment().format('YYYY-MM-DD');
                form.hours = '';
                form.minutes = '';
                form.user =
                    task.users.find(
                        (user) => user.id === currentUser.value.id,
                    ) ?? task.users[0];
                form.description = '';
            }
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            console.error(error);
        });
}

watch(isOpen, (value) => {
    if (value) {
        updateData();
    }
});
</script>

<template>
    <DialogDrawer v-model="isOpen" size="extra-large" :loading="loading">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title>
            <template v-if="taskTime">
                Mettre à jour le temps #{{ taskTime.id }} de la tâche #{{
                    task.id
                }}
            </template>

            <template v-else>
                Ajouter du temps à la tâche #{{ task.id }}
            </template>
        </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="2">
                            <ListboxField
                                v-model="form.taskType"
                                field-id="taskType"
                                field-name="taskType"
                                label="Type"
                                :options="types"
                                required
                                :disabled="task.isChild"
                            >
                                <template #buttonContent>
                                    {{ form.taskType.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.taskTheme"
                                field-id="taskTheme"
                                field-name="taskTheme"
                                label="Thème"
                                :options="themes"
                                required
                                :disabled="task.isChild"
                            >
                                <template #buttonContent>
                                    {{ form.taskTheme.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>
                        </FormGrid>

                        <InputField
                            v-model="form.taskName"
                            label="Nom de la tâche"
                            placeholder="Nom de la tâche"
                            field-name="taskName"
                            field-id="taskName"
                            required
                        >
                            <template #inputPrefix>
                                {{ formattedTaskNamePrefix }}
                            </template>
                        </InputField>
                    </FormContent>

                    <FormContent>
                        <InputField
                            v-model="form.date"
                            label="Date"
                            placeholder="Date"
                            field-name="date"
                            field-id="date"
                            type="date"
                            required
                        />

                        <TimeField
                            v-model:hours="form.hours"
                            v-model:minutes="form.minutes"
                            hours-field-id="hours"
                            hours-field-name="hours"
                            minutes-field-id="minutes"
                            minutes-field-name="minutes"
                            label="Temps"
                            required
                        />
                    </FormContent>

                    <FormContent>
                        <ListboxField
                            v-model="form.user"
                            field-id="user"
                            field-name="user"
                            label="Responsable tâche"
                            :options="task.users"
                            required
                        >
                            <template #buttonContent>
                                {{ form.user.fullName }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.fullName }}
                            </template>
                        </ListboxField>

                        <TextareaField
                            v-model="form.description"
                            label="Description"
                            placeholder="Description"
                            field-name="description"
                            field-id="description"
                        />
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                >
                    <template #actions>
                        <DeleteAction
                            v-if="taskTime"
                            :url="
                                route(
                                    'front.projectManagement.task.deleteTaskTime',
                                    { taskTime: taskTime.id },
                                )
                            "
                            :callback="
                                () => {
                                    isOpen = false;

                                    if (callback) {
                                        callback();
                                    }
                                }
                            "
                        >
                            <template #trigger>
                                <ActionButton type="transparent">
                                    Supprimer
                                </ActionButton>
                            </template>
                        </DeleteAction>
                    </template>
                </FormFooter>
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
